import { UIIcon } from '../config/icons';
import { makeFriendCircle } from '../lib/ab/friends';
import { ab_date_filter_keys, makeDateCorner } from '../lib/ab/widgets-home';
import { aTrackSearch } from '../lib/mdl/a-track';
import { cBookingsFilterActive, cBookingsHomeView } from '../lib/mdl/c-booking-home';
import { c_event_tags, cEventTagName } from '../lib/mdl/c-event-tag';
import { c_venues } from '../lib/mdl/c-venue';
import { the_links } from '../lib/mdl/f-links';
import { LegacyEvent, legacyEventsByCity, legacyEventsGetOnline } from '../lib/mdl/zzz-legacy-open-events';
import { meLogout, the_login_scope, the_user } from '../lib/me/login';
import { getCountry } from '../lib/me/me';
import { hasSubscriptions, meGetStatus } from '../lib/me/home';
import { UIAside } from '../lib/ui/aside';
import { onAttach, onDetach } from '../lib/ui/attach';
import { UIDataAdapter } from '../lib/ui/component/adapter';
import { UIButton } from '../lib/ui/component/button';
import { UIDateRangePicker } from '../lib/ui/component/date';
import { UIImageView } from '../lib/ui/component/image';
import { UITextInput } from '../lib/ui/component/input';
import { UIList } from '../lib/ui/component/list';
import { $el } from '../lib/ui/elements';
import { UIMenu, uiShowMenu } from '../lib/ui/menu';
import { uiHidePopup, uiShowPopup } from '../lib/ui/popup';
import { UIClassesSpec, UIRelPos } from '../lib/ui/types';
import { UIViewAddSpec, UIView } from '../lib/ui/view';
import { $analytics } from '../lib/y/analytics';
import { $on } from '../lib/y/events';
import { $fn } from '../lib/y/fn';
import { $i18n } from '../lib/y/i18n';
import { $location } from '../lib/y/location';
import { $id, YObjectId } from '../lib/y/types';
import { xavis_experiments } from '../lib/y/debug';

interface HomeMenuOptions {
    classes?: UIClassesSpec;
    no_login?: boolean;
    no_logout?: boolean;
    is_signup?: boolean;
}

export let home_banner_dismissed = false;

class HamburgerMenu extends UIAside {
    _toggle: () => void;

    constructor(toggle: () => void) {
        super('tl-hamburger-menu');

        this._toggle = toggle;

        const makeButton = (title: UIViewAddSpec, location?: string[], action?: () => void, link?: string, icon?: UIIcon) =>
            new UIButton({
                kind: 'k-text', title: title, left_icon: icon, link: link, on_click: () => {
                    toggle();
                    if(action)
                        action();
                    if(location)
                        $location.navigate(location);
                }
            });

        if(the_user) {
            this.ui_add($el.DIV('r-buttons', [
                makeButton([$el.DIV('tl-icon m-event'), $i18n.tl.events], []),
                makeButton($i18n.tl.play, ['play'], undefined, undefined, UIIcon.PLAY | UIIcon.WEIGHT_SOLID),
                $el.RULER(),
                makeButton($i18n.tl.m_profile, ['account']),
                hasSubscriptions() && makeButton($i18n.tl.m_bookings, ['bookings']),
                makeButton($i18n.tl.m_membership, ['membership']),
                hasSubscriptions() && makeButton($i18n.tl.m_friends, ['invite']),
                makeButton($i18n.tl.m_giftcard, ['giftcard']),
                makeButton($i18n.tl.m_help_center, undefined, undefined, getCountry() == 'DE' ? the_links.support_de : the_links.support),
                makeButton($i18n.tl.m_logout, [], () => { meLogout() })
            ]));
        } else {
            this.ui_add($el.DIV('r-buttons', [
                makeButton($i18n.tl.how_it_works, ['how']),
                makeButton($i18n.tl.m_giftcard, ['giftcard']),
                makeButton($i18n.tl.past, ['past']),
                makeButton($i18n.tl.faq, ['faq']),
                makeButton($i18n.tl.about_us, ['about']),
                makeButton($i18n.tl.become_member, ['signup'])
            ]));
        }

    }

    override present() {
        return super.present({ position: 'left', backdrop_kind: 'darker', on_click: this._toggle, low: true });
    }
}

class SearchCard extends UIView {
    constructor(event: LegacyEvent, toggle: () => void) {
        super('div', 'tl-search-card');

        const model = event.model;
        const venue = c_venues.maybe($id.objectIdToId(event.model.venue as YObjectId))!;

        const next_session = event.next_session;
        const next_session_text = event.getNextSessionText();

        const genres = event.genre_tags;
        const without_play = genres.filter(g => g.id != '5e8f992cbf50f572a04e7cc9');
        const is_play = genres.length != without_play.length;

        this.ui_add([
            new UIImageView({ media: model.pictures?.[0], variant: 'size-500' }),
            next_session && makeDateCorner(next_session.time_y, 'm-mobile-small'),
            $el.DIV('r-texts', [
                $el.DIV('r-title', event.name),
                $el.S(),
                next_session_text && $el.DIV('r-info', [$el.ICON(UIIcon.CLOCK | UIIcon.WEIGHT_SOLID), next_session_text]),
                $id.idToObjectId(venue.id) != '5e7d513abf50f5490b7e9200' && $el.DIV('r-info', [$el.ICON(UIIcon.LOCATION_DOT | UIIcon.WEIGHT_SOLID), venue.name]),
                $el.DIV(['r-info', 'm-genre'], [
                    is_play ? $el.ICON(UIIcon.PLAY | UIIcon.WEIGHT_SOLID) : $el.DIV('tl-icon m-event'),
                    without_play.length ? without_play.slice(0, 2).map(g => $el.I(cEventTagName(g) + '\u00a0\u00a0\u00a0')) : $el.I('Övrigt')
                ])
            ])
        ]);

        this.root.addEventListener('click', () => {
            toggle();
            $location.navigate(event.screen_location, { flow: 'search' });
        });
    }
}

class SearchOverlay extends UIAside {
    _closeSearch: () => void;

    constructor(closeSearch: () => void) {
        super('tl-search-overlay tl-close-search');
        this._closeSearch = closeSearch;

        const input = new UITextInput({ kind: 'k-inline', placeholder: $i18n.tl.ph_search });

        let last_query: string | undefined;
        const doSearch = (v: string) => {
            last_query = v;
            Promise.all([legacyEventsByCity(the_user!.city_id), legacyEventsGetOnline()]).then(r => {
                const events = r[0].concat(r[1]);
                if(v !== last_query) return;
                const num_regexp = /^\d+$/
                const words = v.split(' ').filter($fn.ID).map(w => num_regexp.test(w) ? ' ' + w + ' ' : w.toLocaleLowerCase());
                adapter.data = events.filter(e => (!!e.next_bookable_session || e.model.videos?.length) && e.matchesQuery(words));
            });
        }

        $on(input, 'value', v => {
            if(v === last_query) return;
            last_query = v;

            this.classList.add('f-has-query');
            doSearch(v);

            if(v) {
                setTimeout(() => {
                    if(v === last_query) {
                        $analytics.trackSearch(v);
                        aTrackSearch(v);
                    }
                }, 1000)
            }
        });

        input.on_enter = () => {
            const text = input.value.trim();
            closeSearch();
            if(text)
                $location.navigate(['search'], { text: text });
        }

        const genres = c_event_tags.all.filter(t => t.is_genre);
        const adapter = new UIDataAdapter((e: LegacyEvent) => new SearchCard(e, closeSearch));
        const list = new UIList(adapter, { progressive: true, classes: ['tl-close-search', 'tl-search-list', 'w-block'] })

        this.root.addEventListener('click', ev => {
            if((ev.target as HTMLElement).classList.contains('tl-close-search'))
                closeSearch();
        });

        const calendar_button = new UIButton({
            icon: UIIcon.CALENDAR, kind: 'k-default', classes: 'm-round m-hollow ui-tint-reverse m-calendar', on_click: () => {
                const picker = new UIDateRangePicker;
                $on(picker, 'selected', ([from, to]) => {
                    uiHidePopup(picker);
                    closeSearch();
                    $location.navigate(['search'], { from: from.start.n, to: to.end.n });
                });
                uiShowPopup(picker, calendar_button, { position: UIRelPos.Below | UIRelPos.Start, capture_mouse: true, can_flip: true });
            }
        })

        this.ui_add([
            new UIButton({ icon: UIIcon.XMARK, on_click: closeSearch }),
            $el.DIV('ui-section tl-close-search', [
                $el.DIV('w-block l-form m-center tl-close-search', [
                    input,
                    $el.H3('heading', $i18n.tl.h_filter_date),
                    $el.DIV(['r-options', 'i-input'], [
                        calendar_button,
                        ab_date_filter_keys.map(k => new UIButton({
                            title: $i18n.tl.o_filter_dates[k], classes: 'm-round m-hollow ui-tint-reverse', on_click: () => {
                                closeSearch();
                                $location.navigate(['search'], { date: k });
                            }
                        }))
                    ]),
                    $el.H3('heading', $i18n.tl.h_filter_category),
                    //TODOURGENT: use new user model
                    $el.DIV(['r-options', 'i-input'], genres.sort((a, b) => (the_user?.event_tag_ids.includes(a.id) ? 0 : 1) - (the_user?.event_tag_ids.includes(b.id) ? 0 : 1)).map(v => {
                        return new UIButton({
                            title: cEventTagName(v), classes: 'm-round m-hollow ui-tint-reverse', on_click: () => {
                                closeSearch();
                                $location.navigate(['search'], { tag: v.id });
                            }
                        });
                    })),
                ]),
                list
            ]),
        ]);

        onAttach(this, () => { input!.focus() });
    }

    override present() {
        return super.present({ position: 'overlay', on_click: this._closeSearch, low: true, backdrop_kind: 'none' });
    }
}

class NewSearchOverlay extends UIAside {
    _closeSearch: () => void;

    constructor(closeSearch: () => void) {
        super('tl-search-overlay tl-close-search');
        this._closeSearch = closeSearch;

        const input = new UITextInput({ kind: 'k-inline', placeholder: $i18n.tl.ph_search });

        let last_query: string | undefined;
        const doSearch = (v: string) => {
            last_query = v;
            Promise.all([legacyEventsByCity(the_user!.city_id), legacyEventsGetOnline()]).then(r => {
                const events = r[0].concat(r[1]);
                if(v !== last_query) return;
                const num_regexp = /^\d+$/
                const words = v.split(' ').filter($fn.ID).map(w => num_regexp.test(w) ? ' ' + w + ' ' : w.toLocaleLowerCase());
                adapter.data = events.filter(e => (!!e.next_bookable_session || e.model.videos?.length) && e.matchesQuery(words));
            });
        }

        $on(input, 'value', v => {
            if(v === last_query) return;
            last_query = v;

            this.classList.add('f-has-query');
            doSearch(v);

            if(v) {
                setTimeout(() => {
                    if(v === last_query) {
                        $analytics.trackSearch(v);
                        aTrackSearch(v);
                    }
                }, 1000)
            }
        });

        input.on_enter = () => {
            const text = input.value.trim();
            closeSearch();
            if(text)
                $location.navigate(['search'], { text: text });
        }

        const genres = c_event_tags.all.filter(t => t.is_genre);
        const adapter = new UIDataAdapter((e: LegacyEvent) => new SearchCard(e, closeSearch));
        const list = new UIList(adapter, { progressive: true, classes: ['tl-close-search', 'tl-search-list', 'w-block'] })

        this.root.addEventListener('click', ev => {
            if((ev.target as HTMLElement).classList.contains('tl-close-search'))
                closeSearch();
        });

        const calendar_button = new UIButton({
            icon: UIIcon.CALENDAR, kind: 'k-default', classes: 'm-round m-hollow ui-tint-reverse m-calendar', on_click: () => {
                const picker = new UIDateRangePicker;
                $on(picker, 'selected', ([from, to]) => {
                    uiHidePopup(picker);
                    closeSearch();
                    $location.navigate(['search'], { from: from.start.n, to: to.end.n });
                });
                uiShowPopup(picker, calendar_button, { position: UIRelPos.Below | UIRelPos.Start, capture_mouse: true, can_flip: true });
            }
        })

        this.ui_add([
            new UIButton({ icon: UIIcon.XMARK, on_click: closeSearch }),
            $el.DIV('ui-section tl-close-search', [
                $el.DIV('w-block l-form m-center tl-close-search', [
                    input,
                    $el.H3('heading', $i18n.tl.h_filter_date),
                    $el.DIV(['r-options', 'i-input'], [
                        calendar_button,
                        ab_date_filter_keys.map(k => new UIButton({
                            title: $i18n.tl.o_filter_dates[k], classes: 'm-round m-hollow ui-tint-reverse', on_click: () => {
                                closeSearch();
                                $location.navigate(['search'], { date: k });
                            }
                        }))
                    ]),
                    $el.H3('heading', $i18n.tl.h_filter_category),
                    //TODOURGENT: use new user model
                    $el.DIV(['r-options', 'i-input'], genres.sort((a, b) => (the_user?.event_tag_ids.includes(a.id) ? 0 : 1) - (the_user?.event_tag_ids?.includes(b.id) ? 0 : 1)).map(v => {
                        return new UIButton({
                            title: cEventTagName(v), classes: 'm-round m-hollow ui-tint-reverse', on_click: () => {
                                closeSearch();
                                $location.navigate(['search'], { tag: v.id });
                            }
                        });
                    })),
                ]),
                list
            ]),
        ]);

        onAttach(this, () => { input!.focus() });
    }

    override present() {
        return super.present({ position: 'overlay', on_click: this._closeSearch, low: true, backdrop_kind: 'none' });
    }
}

export class HomeMenu extends UIView {
    signup_button?: UIButton;

    constructor(options: HomeMenuOptions = {}) {
        super('header', ['tl-menu', options.classes]);

        let hamburger_menu: HamburgerMenu | undefined;
        const toggle_hamburger = () => {
            if(hamburger_menu) {
                hamburger_menu.dismiss(true);
                hamburger_menu = undefined;
                hamburger.checked = false;
            } else {
                this.classList.add('f-hamburger');
                this.classList.remove('f-search');
                hamburger.checked = true;
                hamburger_menu = new HamburgerMenu(toggle_hamburger);
                hamburger_menu.present();
            }
        };

        const hamburger = new UIButton({ kind: 'k-hamburger', title: 'MENY', alt: 'Meny', on_click: toggle_hamburger });

        onDetach(this, () => {
            if(hamburger_menu) {
                hamburger_menu.dismiss(true);
                hamburger_menu = undefined;
            }
        });

        const logo = $el.DIV('r-logo');
        logo.addEventListener('click', () => {
            $location.navigate([]);
        });

        const events_button = new UIButton({ title: [$el.DIV('tl-icon m-event'), $i18n.tl.events], kind: 'k-text', classes: 'r-nav', link: [] });
        const play_button = new UIButton({ title: $i18n.tl.play, left_icon: UIIcon.PLAY | UIIcon.WEIGHT_SOLID, kind: 'k-text', classes: 'r-nav', link: ['play'] });

        if($location.at([])) {
            play_button.classList.add('f-dim');
        } else if($location.at(['play'])) {
            events_button.classList.add('f-dim');
        }

        this.ui_add([
            hamburger,
            $el.S(undefined, 'q-m'),
            logo,
            the_login_scope && getCountry() != 'DE' && events_button,
            the_login_scope && getCountry() != 'DE' && play_button,
            $el.S()
        ]);

        if(the_login_scope) {
            if(options.is_signup) {
                if(!options.no_logout) {
                    this.ui_add(new UIButton({
                        title: 'Logga ut', kind: 'k-text', on_click: () => {
                            meLogout();
                            $location.navigate([]);
                        }
                    }));
                }
            } else {
                const user = the_user!;

                const status = meGetStatus();
                if(status == 'pastdue')
                    this.showBanner($i18n.tl.bnr_pastdue, $i18n.tl.bnr_pastdue_b);
                else if(status == 'pending_cancel')
                    this.showBanner($i18n.tl.bnr_pending_cancel, $i18n.tl.bnr_pending_cancel_b);
                else if(status == 'none')
                    this.showBanner($i18n.tl.bnr_none, $i18n.tl.bnr_none_b);
                else if(status == 'cancelled')
                    this.showBanner($i18n.tl.bnr_cancelled, $i18n.tl.bnr_cancelled_b);
                else if(status == 'paused')
                    this.showBanner($i18n.tl.bnr_paused, $i18n.tl.bnr_paused_b);

                let overlay: SearchOverlay | NewSearchOverlay | undefined;

                const openSearch = () => {
                    if(!overlay) {
                        this.classList.add('f-search');
                        this.classList.remove('f-hamburger');

                        overlay = xavis_experiments ? new NewSearchOverlay(closeSearch) : new SearchOverlay(closeSearch);
                        overlay.present();
                    }
                }

                const closeSearch = () => {
                    if(overlay) {
                        overlay.dismiss(true);
                        overlay = undefined;
                    }
                }

                const search_button = new UIButton({ icon: UIIcon.MAGNIFYING_GLASS, classes: 'r-search', alt: 'Search', on_click: openSearch });

                const account_button = $el.DIV('tl-account-button', [
                    makeFriendCircle(user.first_name, user.last_name, user.avatar_id),
                    $el.ICON(UIIcon.BARS)
                ]);

                account_button.addEventListener('click', () => {
                    const menu = new UIMenu([
                        { title: $i18n.tl.m_home, action: () => { $location.navigate([]) } },
                        { title: $i18n.tl.m_profile, action: () => { $location.navigate(['account']) } },
                        { title: $i18n.tl.m_bookings, action: () => { $location.navigate(['bookings']) }, when: hasSubscriptions },
                        { title: $i18n.tl.m_membership, action: () => { $location.navigate(['membership']) } },
                        { title: $i18n.tl.m_friends, action: () => { $location.navigate(['invite']) }, when: hasSubscriptions },
                        { title: $i18n.tl.m_help_center, link: getCountry() == 'DE' ? the_links.support_de : the_links.support },
                        { title: $i18n.tl.m_logout, action: () => { meLogout(); $location.navigate([]) } }
                    ])

                    uiShowMenu(menu, account_button, undefined, undefined, 10);
                });

                this.ui_add([
                    new UIButton({ kind: 'k-text', classes: 'r-link t-uppercase q-d', title: $i18n.tl.m_giftcard, on_click: () => { $location.navigate(['giftcard']) } }),
                    search_button,
                    account_button
                ]);

                cBookingsHomeView().then(bookings => {
                    const active = bookings.filter(cBookingsFilterActive);
                    if(active.length > 0) {
                        const bookings_button = new UIButton({ title: [$el.DIV('tl-icon m-ticket'), '' + active.length], kind: 'k-text', classes: 'r-bookings', link: ['bookings'] });
                        this.ui_insert(bookings_button, search_button);
                    }
                });
            }
        } else {
            if(!options.no_login) {
                const button_on_click = () => { $location.navigate(['login']); };

                this.ui_add([
                    new UIButton({ kind: 'k-text', classes: 'r-link t-uppercase', title: $i18n.tl.how_it_works, on_click: () => { $location.navigate(['how']) } }),
                    new UIButton({ kind: 'k-text', classes: 'r-link t-uppercase', title: $i18n.tl.m_giftcard, on_click: () => { $location.navigate(['giftcard']) } }),
                    new UIButton({ kind: 'k-text', classes: 'r-link t-uppercase', title: $i18n.tl.about_us, on_click: () => { $location.navigate(['about']) } }),
                    new UIButton({ title: $i18n.tl.m_login, kind: 'k-shadow', classes: 'm-compact r-link t-uppercase ui-tint-blue', on_click: button_on_click }),
                    new UIButton({ icon: UIIcon.RIGHT_TO_BRACKET, classes: 'q-m r-icon', on_click: button_on_click })
                ]);
            }
        }
    }

    showBanner(text: string, button: string) {
        this.classList.add('f-banner');

        const banner = $el.DIV('r-banner c-dark', $el.B([text, ' ', $el.SPAN('t-w-bold', button)]));
        banner.addEventListener('click', () => {
            home_banner_dismissed = true;
            $location.navigate(['membership']);
        });
        this.ui_add(banner);
    }
}
